import { Typography, Stack, Box, styled, Grid, Link } from '@mui/material';
import { ButtonCustom } from '../../../../components/elements/ButtonCustom';
import React from 'react';
import { useSeizureDetails } from './hooks';
import { StyledParams } from 'types/common';
import { LayoutAdminTable } from '../../../../components/templates/LayoutAdminTable';
import { Helmet } from 'react-helmet-async';
import { SummaryLog } from '../../../FileDetail/components/SummaryLog';
import { StorageServices } from 'services/storage';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

type DetailItemProps = {
  title: string;
  content: string | number | boolean | undefined;
};
const DetailItem = ({ title, content }: DetailItemProps) => (
  <WrapperContent>
    <TextTitleStyled>{title}:</TextTitleStyled>
    <TextContentStyled>{content}</TextContentStyled>
  </WrapperContent>
);
export const renderTitle = (status: string) => {
  if (status === 'CLOSE_REDEMPTION') {
    return 'Redeemed';
  }
  if (status === 'CLOSE_CLIENT_REQUEST') {
    return 'Closed client request';
  }
  if (status === 'CLOSE_NOT_FOUND') {
    return 'Closed not found';
  }
  return 'Seized';
};
const storage = new StorageServices();
export const SeizureDetails = () => {
  const { handleOnClick, dataDetail, handleOnBack } = useSeizureDetails();
  const navigate = useNavigate();

  return (
    <LayoutAdminTable
      title={'Seizure Details'}
      element={
        <Stack flexDirection={'row'} gap={2.5}>
          <ButtonCustom
            style={{ width: '150px' }}
            onClick={() => {
              const role = storage.getRole();
              if (['ADMIN', 'ROOT_ADMIN'].includes(role || '')) {
                navigate(`/admin/files/${dataDetail?.fileId}`);
              } else {
                navigate(`/files/${dataDetail?.fileId}`);
              }
            }}
            label={'View File'}
          />
          <ButtonCustom style={{ width: '150px' }} onClick={handleOnBack} label={'Back'} />
        </Stack>
      }>
      <Stack flexDirection={'row'} gap={2.5} mb={2.5}>
        <TextInfo>{`Debtor: ${dataDetail?.file?.lastName} ${dataDetail?.file?.firstName}`}</TextInfo>
        <TextInfo>{`Cobo: ${dataDetail?.file?.coBorrowerFirstName || ''} ${dataDetail?.file?.coBorrowerLastName || ''}`}</TextInfo>
        <TextInfo>{`Client: ${dataDetail?.file?.clientOption?.value}`}</TextInfo>
        <TextInfo>{`Client File Number: ${dataDetail?.file?.fileNumber}`}</TextInfo>
        <TextInfo>{`Reason for closed: ${renderTitle(dataDetail?.file?.fileStatusTransition[0].status || '')}`}</TextInfo>
        <TextInfo>{`Date seizure: ${dataDetail?.createdAt ? dayjs(dataDetail?.createdAt).format('YYYY-MM-DD HH:mm A') : ''}`}</TextInfo>
      </Stack>
      <Helmet>
        <title>Surrey Bailiffs - Seizure Details</title>
      </Helmet>
      <Stack gap={2.5} width={'100%'} flexDirection={'row'}>
        <Wrapper>
          <Stack direction={'row'} gap={2.5} sx={{ flexWrap: 'wrap' }}>
            <DetailItem title="KM" content={dataDetail?.kilometers} />
            <DetailItem
              title="Seize Address"
              content={dataDetail?.addressLead?.address || dataDetail?.otherAddress}
            />
            <DetailItem
              title="Customers POR"
              content={dataDetail?.customersLeadId || dataDetail?.otherCustomerPOR}
            />
            <DetailItem
              title="Removed To"
              content={dataDetail?.removedToLocation?.value || dataDetail?.removedToOther}
            />
            <DetailItem title="Keys" content={dataDetail?.hasKey ? 'Yes' : 'No'} />
            <DetailItem title="Runs" content={dataDetail?.run ? 'Yes' : 'No'} />
            <DetailItem title="Signed VSA" content={dataDetail?.signedVsa ? 'Yes' : 'No'} />
            <DetailItem
              title="Possible Redemption"
              content={dataDetail?.possibleRedemption ? 'Yes' : 'No'}
            />
            <DetailItem title="Condition Report" content={dataDetail?.conditionReport} />
            <DetailItem title="Towing Company Used" content={dataDetail?.towingCompany} />
            <DetailItem
              title="Transport Required"
              content={dataDetail?.transportRequired ? dataDetail?.otherTransport : 'No'}
            />
            <DetailItem
              title="Other Bills Incurred"
              content={dataDetail?.billsIncurred ? dataDetail?.otherBills : 'No'}
            />
          </Stack>

          <Grid container gap={2.5}>
            <Box minWidth={'265px'}>
              <TextTitleStyled sx={{ marginBottom: '16px' }}>Document:</TextTitleStyled>
              <Grid
                style={{ width: '100%' }}
                container
                spacing={1.25}
                justifyContent="flex-start"
                alignItems="center">
                {dataDetail?.vsaDocuments?.map((doc, index) => (
                  <Grid style={{ width: '100%' }} key={index}>
                    <Stack
                      style={{ width: '100%' }}
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}>
                      <Stack flexDirection={'row'} gap={1.5} alignItems={'center'}>
                        <img src={'/icons/icon_document.svg'} alt={'icon'} />
                        <NameDocument onClick={() => window.open(doc.url)}>{doc.name}</NameDocument>
                      </Stack>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              minWidth={'265px'}
              maxWidth={dataDetail?.signedVsa ? '530px' : 'calc(100% - 100px)'}>
              <TextTitleStyled sx={{ marginBottom: '16px' }}>Photos:</TextTitleStyled>
              <Grid container spacing={1.25} justifyContent="flex-start" alignItems="center">
                {dataDetail?.seizureReportPhotos?.map((doc, index) => (
                  <Grid style={{ width: '100%' }} key={index}>
                    <Stack
                      style={{ width: '100%' }}
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}>
                      <Stack flexDirection={'row'} gap={1.5} alignItems={'center'}>
                        <img src={'/icons/icon_document.svg'} alt={'icon'} />
                        <NameDocument onClick={() => window.open(doc.url)}>{doc.name}</NameDocument>
                      </Stack>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Wrapper>
        <Stack width={'36%'} px={2.5} maxHeight={'700px'} overflow={'auto'}>
          {dataDetail?.file?.leads && <SummaryLog leads={dataDetail?.file?.leads} />}
        </Stack>
      </Stack>
      {!!dataDetail?.file?.fileStatusTransition?.length &&
        dataDetail?.file?.fileStatusTransition[0].status !== 'CLOSE_REDEMPTION' && (
          <Stack flexDirection={'row'} justifyContent={'end'}>
            <ButtonCustom
              style={{ width: '250px', marginTop: '20px' }}
              onClick={handleOnClick}
              label={'Move to post seizure'}
            />
          </Stack>
        )}
    </LayoutAdminTable>
  );
};
const TextInfo = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.dark1,
  fontSize: '16px',
  fontWeight: 500
}));
const WrapperContent = styled(Box)(() => ({
  width: '265px'
}));

const Wrapper = styled(Stack)(() => ({
  padding: '40px 30px',
  border: '1px solid #ccc',
  borderRadius: '20px',
  gap: 20,
  flex: 1
}));

const TextTitleStyled = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: '500',
  color: '#000000'
}));

const TextContentStyled = styled(Typography)(({ theme }: StyledParams) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderRadius: '10px',
  marginTop: theme.spacing(0.5),
  minHeight: '50px'
}));

const NameDocument = styled(Link)(({ theme }: StyledParams) => ({
  color: theme.colors.dark1,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer'
}));
